import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight as ChevronRightIcon,
  Clock as ClockIcon,
  Signature as SignatureIcon,
  TrendingUp as TrendingUpIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import type { Vault } from '@/types/vault';
import { formatUsd, getExpiryInfo } from '@/types/vault';

interface SigningRequestLike {
  proposal: { id: string; vaultId: string; expiresAt: string };
}

interface TreasuryHeroProps {
  vaults: Vault[];
  signingRequests: SigningRequestLike[];
}

/**
 * Treasury command-center hero row for the dashboard. Surfaces the three
 * numbers a treasury operator opens the app to check:
 *   1. Total portfolio value across vaults (sum of enriched per-vault USD).
 *   2. Proposals awaiting THIS user's signature (from the signing-requests
 *      already kept in Redux — these are pre-filtered server-side to the
 *      current user's pending signatures).
 *   3. Proposals expiring within 24h (reuses getExpiryInfo urgency logic).
 * Each card deep-links to the relevant page.
 */
export function TreasuryHero({ vaults, signingRequests }: TreasuryHeroProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const totalUsd = useMemo(
    () => vaults.reduce((sum, v) => sum + (v.totalBalanceUsd ?? 0), 0),
    [vaults]
  );

  const awaitingCount = signingRequests.length;

  // Snapshot "now" once on mount — keeps the urgency calc pure across re-renders.
  const [now] = useState(() => Date.now());
  const expiringSoonCount = useMemo(() => {
    return signingRequests.filter((sr) => {
      const info = getExpiryInfo(sr.proposal.expiresAt, now);
      // Within 24h (orange) or already past (red) — both are "act now".
      return !info.isExpired && info.color !== 'default';
    }).length;
  }, [signingRequests, now]);

  return (
    <div className="dashboard-stats" style={{ marginBottom: 20 }}>
      <div
        className="stat-card"
        role="button"
        tabIndex={0}
        onClick={() => void navigate('/portfolio')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') void navigate('/portfolio');
        }}
      >
        <div className="stat-card-header">
          <span className="stat-card-label">{t('dashboard.totalPortfolioValue')}</span>
          <span className="stat-card-icon">
            <TrendingUpIcon />
          </span>
        </div>
        <div className="stat-card-value">
          <span className="privacy-sensitive">{formatUsd(totalUsd)}</span>
        </div>
        <div className="stat-card-subtitle">
          {t('dashboard.acrossVaults', { count: vaults.length })} <ChevronRightIcon />
        </div>
      </div>

      <div
        className="stat-card"
        role="button"
        tabIndex={0}
        onClick={() => void navigate('/signing-requests')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') void navigate('/signing-requests');
        }}
      >
        <div className="stat-card-header">
          <span className="stat-card-label">{t('dashboard.awaitingYourSignature')}</span>
          <span className="stat-card-icon">
            <SignatureIcon />
          </span>
        </div>
        <div
          className="stat-card-value"
          style={awaitingCount > 0 ? { color: 'var(--color-primary)' } : undefined}
        >
          {awaitingCount}
        </div>
        <div className="stat-card-subtitle">
          {t('dashboard.proposalsNeedYou', { count: awaitingCount })} <ChevronRightIcon />
        </div>
      </div>

      <div
        className="stat-card"
        role="button"
        tabIndex={0}
        onClick={() => void navigate('/signing-requests')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') void navigate('/signing-requests');
        }}
      >
        <div className="stat-card-header">
          <span className="stat-card-label">{t('dashboard.expiringWithin24h')}</span>
          <span className="stat-card-icon">
            <ClockIcon />
          </span>
        </div>
        <div
          className="stat-card-value"
          style={expiringSoonCount > 0 ? { color: 'var(--color-error)' } : undefined}
        >
          {expiringSoonCount}
        </div>
        <div className="stat-card-subtitle">
          {t('dashboard.proposalsExpiring', { count: expiringSoonCount })} <ChevronRightIcon />
        </div>
      </div>
    </div>
  );
}

export default TreasuryHero;
